import { useEffect, useRef, useState } from "react";
import Dropzone from "dropzone";
import "dropzone/dist/dropzone.css";
import FaiBox from "../FaiBox";
import FaiDropzoneRoot from "./FaiDropzoneRoot";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

interface FaiDropzoneProps {
	note?: string;
	selectedImages?: string[];
	options?: Dropzone.DropzoneOptions;
	setSelectedImages?: (files: File[], previewUrls: string[]) => void;
}

const FaiDropzone: React.FC<FaiDropzoneProps> = ({
	note = "",
	options = {},
	selectedImages = [],
	setSelectedImages = () => {},
}) => {
	const dropzoneRef = useRef<HTMLFormElement>(null);
	const filesRef = useRef<File[]>([]);
	const [previews, setPreviews] = useState<string[]>(selectedImages);
	const optionsRef = useRef(options);
	const setSelectedImagesRef = useRef(setSelectedImages);

	// Sync selectedImages prop with internal state
	useEffect(() => {
		setPreviews(selectedImages);
	}, [JSON.stringify(selectedImages)]);

	useEffect(() => {
		console.log(1, options)
		optionsRef.current = options;
		setSelectedImagesRef.current = setSelectedImages;
	}, [options, setSelectedImages]);

	useEffect(() => {
		Dropzone.autoDiscover = false;

		let dz: Dropzone | null = null;

		if (dropzoneRef.current) {
			dz = new Dropzone(dropzoneRef.current, {
				...optionsRef.current,
				autoProcessQueue: false,
				addRemoveLinks: true,
				uploadMultiple: true,
				acceptedFiles: "image/*",
			});

			dz.on("addedfile", (file: File) => {
				filesRef.current.push(file);

				const reader = new FileReader();
				reader.onload = () => {
					if (typeof reader.result === "string") {
						setPreviews((prevPreviews) => {
							const newPreviews = [...prevPreviews, reader.result as string];
							setSelectedImagesRef.current(filesRef.current, newPreviews);
							return newPreviews;
						});
					}
				};
				reader.readAsDataURL(file);
			});

			dz.on("removedfile", (file: File) => {
				const index = filesRef.current.indexOf(file);
				if (index !== -1) {
					filesRef.current.splice(index, 1);
					setPreviews((prevPreviews) => {
						const newPreviews = prevPreviews.filter((_, i) => i !== index);
						setSelectedImagesRef.current(filesRef.current, newPreviews);
						return newPreviews;
					});
				}
			});
		}

		return () => {
			dz?.destroy();
		};
	}, []); // Only run on mount

	const handleRemovePreview = (idx: number) => {
		filesRef.current.splice(idx, 1);
		setPreviews((prevPreviews) => {
			const newPreviews = prevPreviews.filter((_, i) => i !== idx);
			setSelectedImages(filesRef.current, newPreviews);
			return newPreviews;
		});
	};

	if (previews.length > 0) {
		return (
			<FaiBox className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
				{previews.map((imgSrc, idx) => (
					<FaiBox key={idx} className="relative border rounded-lg h-40">
						<div
							style={{
								position: "absolute",
								top: "-10px",
								right: "-10px",
								cursor: "pointer",
								background: "var(--theme-color)",
								color: "white",
								borderRadius: "50%",
								width: "24px",
								height: "24px",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								boxShadow: "0 0 5px rgba(0,0,0,0.2)",
							}}
							onClick={() => handleRemovePreview(idx)}
						>
							<span style={{ fontSize: "16px", fontWeight: "bold" }}>
								&times;
							</span>
						</div>
						<img
							src={imgSrc}
							alt={`Preview ${idx}`}
							className="mx-auto max-h-36 mt-2"
							style={{ objectFit: "contain" }}
						/>
					</FaiBox>
				))}
			</FaiBox>
		);
	}

	return (
		<FaiDropzoneRoot
			component="form"
			action="/file-upload"
			ref={dropzoneRef}
			className="form-control dropzone"
			style={{ background: "#ebebeb" }}
		>
			<CloudUploadIcon style={{ fontSize: "48px", color: "#888" }} />
			<FaiBox className="dz-default dz-message">
				<FaiBox component="span" className="dz-button">
					Drag your file(s) or browse
				</FaiBox>
			</FaiBox>
			<FaiBox className="max-size-text">{note || ""}</FaiBox>
			<FaiBox className="fallback">
				<FaiBox
					component="input"
					name="file"
					type="file"
					multiple={options.uploadMultiple}
				/>
			</FaiBox>
		</FaiDropzoneRoot>
	);
};

export default FaiDropzone;
